import * as Preferences from "./preference";
import { SectionIdDefaultValue } from "./preferenceController";
import { lightTheme, darkTheme } from "../sdServices/display-theme-service";
import { ServiceDeskConfig } from "../Startup/serviceDeskConfig";

/**
 * Preference element configs and a tracking value (current value) in order to be able to tell when changes have been made from what the initial preference config value was originally
 */
export interface PreferenceElementWithCurrentValue {
  currentValue: any;
  configured: Preferences.PreferenceElement;
}
/**
 * Specifics for a particular section (preference pane) of preference page
 */
export type Configuration = {
  sectionConfiguration: Preferences.SectionConfiguration;
  preferenceElements: Preferences.PreferenceElement[];
};

const emailPivotCount5 = "5";
const emailPivotCount10 = "10";
const emailDisplayOptionWindow = "window";
const emailDisplayOptionPivot = "pivot";
const caseDisplayOptionWindow = "newwindow";
const caseDisplayOptionTab = "newtab";
const defaultLanguageConstant = "en-US";
const shellCommonAppId = "ShellCommon";

/**
 * A constant that provides the functionality to register preference configs for consumption of preference ui at config time
 */
export class ConfigurationRegistry {
  public configurations: Configuration[] = [];

  /*
   * Populates and registers complete list of Preference Configs based on what is provided
   */
  public registerPreferences(
    sectionConfiguration: Preferences.SectionConfiguration,
    preferenceElements: Preferences.PreferenceElement[]
  ) {
    let filteredSectionConfiguration: Preferences.SectionConfiguration;
    if (sectionConfiguration.sectionId) {
      filteredSectionConfiguration = sectionConfiguration;
    } else {
      filteredSectionConfiguration = {
        stem: sectionConfiguration.stem,
        appId: sectionConfiguration.appId,
        titleLocKey: sectionConfiguration.titleLocKey,
        sectionId: SectionIdDefaultValue,
        associatedStates: sectionConfiguration.associatedStates,
        flightName: sectionConfiguration.flightName,
        automationId: sectionConfiguration.automationId
      };
    }
    this.configurations.push({
      sectionConfiguration: filteredSectionConfiguration,
      preferenceElements: preferenceElements
    });
  }

  /*
   * Returns a complete list of all of the Pane Items that have been registered
   */
  public getSectionConfigurations(): Preferences.SectionConfiguration[] {
    return this.configurations.map(function (preferenceConfiguration: Configuration) {
      return preferenceConfiguration.sectionConfiguration;
    });
  }
}

export class LanguageProvider {
  public defaultSystemLanguage: string;

  public getBrowserLanguage($windowProvider: any): string {
    let window = $windowProvider.$get();
    // default Language
    let browserLanguage = defaultLanguageConstant;
    if (window && window.navigator) {
      browserLanguage = window.navigator.language;
    }

    return browserLanguage;
  }

  public getDefaultSettingLanguage(languages: string[], $windowProvider: any) {
    let defaultLanguage = defaultLanguageConstant.toLowerCase();
    languages.forEach((language: string) => {
      if (language.includes(this.getBrowserLanguage($windowProvider).toLowerCase())) {
        defaultLanguage = language;
      }
    });

    return defaultLanguage;
  }

  public getCorrectMapping(language: string): string {
    // to be in sync with {sourceRoot}\ShellCommon\localization\languages\Loc\en-us.json
    let localizedLanguageKey: string;
    switch (language) {
      case "zh-cn":
        localizedLanguageKey = "zh-Hans";
        break;
      case "zh-tw":
        localizedLanguageKey = "zh-Hant";
        break;
      case "pt-br":
        localizedLanguageKey = "pt";
        break;
      default:
        localizedLanguageKey = language;
    }
    return localizedLanguageKey;
  }

  public getLanguageOptions($windowProvider: any, sdConfig: ServiceDeskConfig): Preferences.PreferenceOption[] {
    let languages: string[] = [];
    languages.push(defaultLanguageConstant.toLowerCase());
    let manifestRevisions = sdConfig.manifestRevisions;
    let shellCommonLanguages = manifestRevisions.ShellCommon.Loc;
    Object.keys(shellCommonLanguages).forEach((language: string) => {
      if (language != "ps-LOC" && language != defaultLanguageConstant.toLowerCase()) {
        languages.push(language.toLowerCase());
      }
    });

    this.defaultSystemLanguage = this.getDefaultSettingLanguage(languages, $windowProvider);
    let languageOptions: Preferences.PreferenceOption[] = [];
    languageOptions.push({
      locData: [
        { locKey: "_PREFERENCES_GENERAL_LANGUAGE_SYSTEM_DEFAULT_" },
        { locKey: this.getCorrectMapping(this.defaultSystemLanguage), locAppId: shellCommonAppId }
      ],
      value: this.defaultSystemLanguage
    });

    languages.forEach((language: string) => {
      if (language != this.defaultSystemLanguage) {
        languageOptions.push({
          locData: [{ locKey: this.getCorrectMapping(language), locAppId: shellCommonAppId }],
          value: language
        });
      }
    });

    if (sdConfig.currentEnvironment && sdConfig.currentEnvironment != "prod") {
      languageOptions.push({ locData: [{ locKey: "_PREFERENCES_GENERAL_LANGUAGE_PS_LOC_" }], value: "ps-loc" });
    }

    return languageOptions;
  }
}

export class PreferenceConfiguration {
  // @ngInject
  constructor(
    $windowProvider: any,
    $injector: ng.auto.IInjectorService,
    preferenceConfigurationRegistry: ConfigurationRegistry,
    preferenceLanguageProvider: LanguageProvider,
    sdConfig: ServiceDeskConfig
  ) {
    let caseWindowOptions: Preferences.PreferenceOption[] = [
      {
        locData: [
          { locKey: "_PREFERENCES_GENERAL_LANGUAGE_SYSTEM_DEFAULT_" },
          { locKey: "_PREFERENCES_GENERAL_CONTENT_DISPLAY_TAB_" }
        ],
        value: caseDisplayOptionTab
      },
      { locData: [{ locKey: "_PREFERENCES_GENERAL_CONTENT_DISPLAY_WINDOW_" }], value: caseDisplayOptionWindow }
    ];

    let emailWindowOptionsDefaultPivot: Preferences.PreferenceOption[] = [
      {
        locData: [
          { locKey: "_PREFERENCES_GENERAL_EMAIL_SYSTEM_DEFAULT_" },
          { locKey: "_PREFERENCES_OPEN_EMAIL_DISPLAY_PIVOT_" }
        ],
        value: emailDisplayOptionPivot
      },
      { locData: [{ locKey: "_PREFERENCES_OPEN_EMAIL_DISPLAY_WINDOW_" }], value: emailDisplayOptionWindow }
    ];

    let emailWindowOptionsDefaultWindow: Preferences.PreferenceOption[] = [
      {
        locData: [
          { locKey: "_PREFERENCES_GENERAL_EMAIL_SYSTEM_DEFAULT_" },
          { locKey: "_PREFERENCES_OPEN_EMAIL_DISPLAY_WINDOW_" }
        ],
        value: emailDisplayOptionWindow
      },
      { locData: [{ locKey: "_PREFERENCES_OPEN_EMAIL_DISPLAY_PIVOT_" }], value: emailDisplayOptionPivot }
    ];

    let emailPivotCountOptions: Preferences.PreferenceOption[] = [
      {
        locData: [
          { locKey: "_PREFERENCES_EMAIL_PIVOT_COUNT_DEFAULT_" },
          { locKey: "_PREFERENCES_EMAIL_PIVOT_COUNT_5_" }
        ],
        value: emailPivotCount5
      },
      { locData: [{ locKey: "_PREFERENCES_EMAIL_PIVOT_COUNT_10_" }], value: emailPivotCount10 }
    ];

    let themeOptions: Preferences.PreferenceOption[] = [
      { locData: [{ locKey: "_PREFERENCES_GENERAL_THEME_LIGHT_" }], value: lightTheme },
      { locData: [{ locKey: "_PREFERENCES_GENERAL_THEME_DARK_" }], value: darkTheme }
    ];

    preferenceConfigurationRegistry.registerPreferences(
      {
        stem: "general",
        appId: "ServiceDesk",
        titleLocKey: "_PREFERENCES_GENERAL_TITLE_",
        associatedStates: ["??"],
        automationId: "preferences-general-pivot"
      },
      [
        // TODO: look up what associatedStates does.
        new Preferences.Heading("_PREFERENCES_GENERAL_LANGUAGE_HEADER_").behindFlight("Localization"),

        new Preferences.HorizontalRuler().behindFlight("Localization"),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_GENERAL_LANGUAGE_PROMPT_",
          preferenceKey: "language",
          initialValue: preferenceLanguageProvider.defaultSystemLanguage,
          preferenceOptions: preferenceLanguageProvider.getLanguageOptions($windowProvider, sdConfig),
          sortOptions: false,
          preventMidSessionUpdate: true
        }).behindFlight("Localization"),

        new Preferences.SectionBreak().behindFlight("Localization"),

        new Preferences.Heading("_PREFERENCES_GENERAL_THEME_HEADER_").behindFlight("Preference_Theme"),

        new Preferences.HorizontalRuler().behindFlight("Preference_Theme"),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_GENERAL_THEME_LABEL_",
          preferenceKey: "displayTheme",
          initialValue: lightTheme,
          preferenceOptions: themeOptions,
          sortOptions: false,
          preventMidSessionUpdate: true
        }).behindFlight("Preference_Theme"),

        new Preferences.SectionBreak().behindFlight("Preference_Theme"),

        new Preferences.Heading("_PREFERENCES_GENERAL_CONTENT_HEADER_"),

        new Preferences.HorizontalRuler(),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_GENERAL_CONTENT_DISPLAY_PROMPT_",
          preferenceKey: "caseDisplayOption",
          initialValue: caseDisplayOptionTab,
          preferenceOptions: caseWindowOptions
        }),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_GENERAL_EMAIL_DISPLAY_PROMPT_",
          preferenceKey: "emailDisplayOption",
          initialValue: emailDisplayOptionPivot,
          preferenceOptions: emailWindowOptionsDefaultPivot
        }).behindFlight("EMAIL_DEFAULT_PIVOT"),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_GENERAL_EMAIL_DISPLAY_PROMPT_",
          preferenceKey: "emailDisplayOption",
          initialValue: emailDisplayOptionWindow,
          preferenceOptions: emailWindowOptionsDefaultWindow
        }).behindFlight("EMAIL_DEFAULT_WINDOW"),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_GENERAL_EMAIL_PIVOT_COUNT_",
          preferenceKey: "emailPivotCountOption",
          initialValue: emailPivotCount5,
          preferenceOptions: emailPivotCountOptions
        }).behindFlight("EMAIL_PIVOT_DISPLAY"),

        new Preferences.SectionBreak(),

        new Preferences.Heading("_PREFERENCES_GENERAL_CASECOLOR_HEADER_").behindFlight("HI_ColorCoding"),

        new Preferences.HorizontalRuler().behindFlight("HI_ColorCoding"),

        new Preferences.Label("_PREFERENCES_GENERAL_CASECOLOR_LABEL_").behindFlight("HI_ColorCoding"),

        new Preferences.LocalizedLink(
          { descriptionLocKey: "_PREFERENCES_GENERAL_CASECOLOR_TITLE_" },
          "/#/home/categorizedcases"
        ).behindFlight("HI_ColorCoding"),

        new Preferences.SectionBreak().behindFlight("HI_ColorCoding"),

        new Preferences.Heading("_PREFERENCES_GENERAL_SETTINGS_HEADER_").behindFlight("Preference_Delete_All"),

        new Preferences.HorizontalRuler().behindFlight("Preference_Delete_All"),

        new Preferences.Label("_PREFERENCES_GENERAL_SETTINGS_LABEL_").behindFlight("Preference_Delete_All"),

        new Preferences.DeleteAllButton({
          descriptionLocKey: "_PREFERENCES_GENERAL_SETTINGS_DELETE_TITLE_"
        }).behindFlight("Preference_Delete_All")
      ]
    );

    let chatSounds: Preferences.PreferenceOption[] = [
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT01_" }],
        value: "../ServiceDesk/Assets/sounds/Chat01.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT02_" }],
        value: "../ServiceDesk/Assets/sounds/Chat02.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT03_" }],
        value: "../ServiceDesk/Assets/sounds/Chat03.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT04_" }],
        value: "../ServiceDesk/Assets/sounds/Chat04.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT05_" }],
        value: "../ServiceDesk/Assets/sounds/Chat05.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT06_" }],
        value: "../ServiceDesk/Assets/sounds/Chat06.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT07_" }],
        value: "../ServiceDesk/Assets/sounds/Chat07.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT08_" }],
        value: "../ServiceDesk/Assets/sounds/Chat08.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT09_" }],
        value: "../ServiceDesk/Assets/sounds/Chat09.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT10_" }],
        value: "../ServiceDesk/Assets/sounds/Chat10.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CHAT11_" }],
        value: "../ServiceDesk/Assets/sounds/Chat11.mp3"
      }
    ];
    let messageSounds: Preferences.PreferenceOption[] = [
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE01_" }],
        value: "../ServiceDesk/Assets/sounds/Message01.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE02_" }],
        value: "../ServiceDesk/Assets/sounds/Message02.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE03_" }],
        value: "../ServiceDesk/Assets/sounds/Message03.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE04_" }],
        value: "../ServiceDesk/Assets/sounds/Message04.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE05_" }],
        value: "../ServiceDesk/Assets/sounds/Message05.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE06_" }],
        value: "../ServiceDesk/Assets/sounds/Message06.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE07_" }],
        value: "../ServiceDesk/Assets/sounds/Message07.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE08_" }],
        value: "../ServiceDesk/Assets/sounds/Message08.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE09_" }],
        value: "../ServiceDesk/Assets/sounds/Message09.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE10_" }],
        value: "../ServiceDesk/Assets/sounds/Message10.mp3"
      },
      {
        locData: [{ locKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_MESSAGE11_" }],
        value: "../ServiceDesk/Assets/sounds/Message11.mp3"
      }
    ];

    preferenceConfigurationRegistry.registerPreferences(
      {
        stem: "comm",
        appId: "Comm",
        titleLocKey: "_PREFERENCES_COMM_TITLE_",
        associatedStates: ["comm"],
        automationId: "preferences-comm-pivot"
      },
      [
        new Preferences.Heading("_PREFERENCES_COMM_CHAT_LAYOUT_HEADER_"),

        new Preferences.HorizontalRuler(),

        new Preferences.PreferenceToggle({
          descriptionLocKey: "_PREFERENCES_COMM_CHAT_LAYOUT_COLLAPSE_TOGGLE_",
          preferenceKey: "collapseInternalChatPanelByDefault",
          initialValue: true
        }),

        new Preferences.SectionBreak(),

        new Preferences.Heading("_PREFERENCES_COMM_SOUNDS_HEADER_"),

        new Preferences.HorizontalRuler(),

        new Preferences.Label("_PREFERENCES_COMM_SOUNDS_PROMPT_"),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_COMM_SOUNDS_CHAT_NEW_REQUEST_",
          preferenceKey: "newchatrequestsound",
          initialValue: "../ServiceDesk/Assets/sounds/Chat08.mp3",
          preferenceOptions: chatSounds,
          selectMenuBehavior: Preferences.Sound
        }),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_COMM_SOUNDS_CHAT_CONSULT_REQUEST_",
          preferenceKey: "consultChatRequestSound",
          initialValue: "../ServiceDesk/Assets/sounds/Chat02.mp3",
          preferenceOptions: chatSounds,
          selectMenuBehavior: Preferences.Sound
        }),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_COMM_SOUNDS_CHAT_REJOIN_REQUEST_",
          preferenceKey: "rejoinChatRequestSound",
          initialValue: "../ServiceDesk/Assets/sounds/Chat05.mp3",
          preferenceOptions: chatSounds,
          selectMenuBehavior: Preferences.Sound
        }),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_NEW_",
          preferenceKey: "newchatmessagesound",
          initialValue: "../ServiceDesk/Assets/sounds/Message06.mp3",
          preferenceOptions: messageSounds,
          selectMenuBehavior: Preferences.Sound
        }),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_COMM_SOUNDS_MESSAGE_END_",
          preferenceKey: "customerendChat",
          initialValue: "../ServiceDesk/Assets/sounds/Message04.mp3",
          preferenceOptions: messageSounds,
          selectMenuBehavior: Preferences.Sound
        })
      ]
    );

    let tileResultCountOptions: Preferences.PreferenceOption[] = [
      { locData: [{ locKey: "_PREFERENCES_HOME_TILE_RESULT_SMALL_" }], value: "10" },
      { locData: [{ locKey: "_PREFERENCES_HOME_TILE_RESULT_MEDIUM_" }], value: "25" },
      { locData: [{ locKey: "_PREFERENCES_HOME_TILE_RESULT_LARGE_" }], value: "50" }
    ];

    preferenceConfigurationRegistry.registerPreferences(
      {
        stem: "home",
        appId: "Home",
        titleLocKey: "_PREFERENCES_HOME_TITLE_",
        associatedStates: ["??"],
        automationId: "preferences-home-pivot"
      },
      [
        // TODO: look up what associatedStates does.
        new Preferences.Heading("_PREFERENCES_HOME_CASES_TITLE_"),

        new Preferences.HorizontalRuler(),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_HOME_TILE_RESULT_COUNT_",
          preferenceKey: "casesTileResultCount",
          initialValue: 10,
          preferenceOptions: tileResultCountOptions,
          sortOptions: false,
          preventMidSessionUpdate: true
        }),

        new Preferences.Heading("_PREFERENCES_HOME_TASKS_TITLE_"),

        new Preferences.HorizontalRuler(),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_HOME_TILE_RESULT_COUNT_",
          preferenceKey: "tasksTileResultCount",
          initialValue: 10,
          preferenceOptions: tileResultCountOptions,
          sortOptions: false,
          preventMidSessionUpdate: true
        }),

        new Preferences.Heading("PREFERENCES_TEAMS_CASES_TASK_UNREADEMAIL_TILE").behindFlight("HI_MyTeamTile"),

        new Preferences.HorizontalRuler().behindFlight("HI_MyTeamTile"),

        new Preferences.PreferenceSelectMenu({
          descriptionLocKey: "_PREFERENCES_HOME_TILE_RESULT_COUNT_",
          preferenceKey: "teamscasesTileResultCount",
          initialValue: 10,
          preferenceOptions: tileResultCountOptions,
          sortOptions: false,
          preventMidSessionUpdate: true
        }).behindFlight("HI_MyTeamTile")
      ]
    );

    preferenceConfigurationRegistry.registerPreferences(
      {
        stem: "about",
        appId: "ServiceDesk",
        titleLocKey: "_PREFERENCES_ABOUT_TITLE_",
        associatedStates: [],
        automationId: "preferences-about-pivot"
      },
      [
        new Preferences.Heading("_PREFERENCES_ABOUT_TITLE_"),

        new Preferences.HorizontalRuler(),

        new Preferences.CustomElement("<sd-about-inner></sd-about-inner>")
      ]
    );

    preferenceConfigurationRegistry.registerPreferences(
      {
        flightName: "SD_RELEASE_NOTES",
        stem: "release_notes",
        appId: "ServiceDesk",
        titleLocKey: "_PREFERENCES_RELEASE_NOTES_TITLE_",
        associatedStates: [],
        automationId: "preferences-release-notes-pivot"
      },
      [
        new Preferences.CustomElement("<sd-detailed-release-notes></sd-detailed-release-notes>").behindFlight(
          "SD_RELEASE_NOTES"
        )
      ]
    );

    preferenceConfigurationRegistry.registerPreferences(
      {
        flightName: "SDK_DIAGNOSTICS",
        stem: "diagnostics",
        appId: "ServiceDesk",
        titleLocKey: "_PREFERENCES_TROUBLESHOOTING_TITLE_",
        associatedStates: [],
        automationId: "preferences-diagnostics-pivot"
      },
      [new Preferences.CustomElement("<sd-diagnostics></sd-diagnostics>").behindFlight("SDK_DIAGNOSTICS")]
    );
  }
}
